import "./index.css";

import { Application, ControllerConstructor } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

import { PWAInstallElement } from "@khmyznikov/pwa-install";
import "./elements/currency_input";

import AccountsController from "./controllers/accounts_controller";
import AccountTooltipController from "./controllers/account_tooltip_controller";
import AccountSetupController from "./controllers/account_setup_controller";
import AlertsController from "./controllers/alerts_controller";
import BalanceCheckController from "./controllers/balance_check_controller";
import PagePositionController from "./controllers/page_position_controller";
import AutoReadNotificationController from "./controllers/auto_read_notification_controller";
import BottomSheetController from "./controllers/bottom_sheet_controller";
import BudgetAllocationBarController from "./controllers/budget_allocation_bar_controller";
import BudgetCategoryFilterController from "./controllers/budget_category_filter_controller";
import NotificationsPanelController from "./controllers/notifications_panel_controller";
import NotificationsTriggerController from "./controllers/notifications_trigger_controller";
import ColoriserController from "./controllers/coloriser_controller";
import DialogController from "./controllers/dialog_controller";
import DonutController from "./controllers/donut_controller";
import CategoriesController from "./controllers/categories_controller";
import CategoriseController from "./controllers/categorise_controller";
import CenterController from "./controllers/center_controller";
import FormRedirectController from "./controllers/form_redirect_controller";
import FormsController from "./controllers/forms_controller";
import InstitutionsController from "./controllers/institutions_controller";
import MonthStripController from "./controllers/month_strip_controller";
import PayloadExampleController from "./controllers/payload_example_controller";
import PushController from "./controllers/push_controller";
import QuickCategorisationController from "./controllers/quick_categorisation_controller";
import PwaController from "./controllers/pwa_controller";
import TurnstileController from "./controllers/turnstile_controller";
import ScrollToTopController from "./controllers/scroll_to_top_controller";
import SelectsController from "./controllers/selects_controller";
import SvgBarChartController from "./controllers/svg_bar_chart_controller";
import TabsController from "./controllers/tabs_controller";
import TransactionAnalysisController from "./controllers/transaction_analysis_controller";
import TransactionFiltersController from "./controllers/transaction_filters_controller";
import TurboRefreshController from "./controllers/turbo_refresh_controller";
import FrameSpinnerController from "./controllers/frame_spinner_controller";
import ToggleController from "./controllers/toggle_controller";

declare global {
  interface Window {
    Stimulus: Application;
    BudgeterConfig: {
      uncategorised_category_id: string;
      decimal: string;
      group: string;
      sw_cache_key: string;
    };
  }
}

if (!customElements.get("pwa-install")) {
  customElements.define("pwa-install", PWAInstallElement);
}

// Keep a list of the controllers that we register, so we can also unload them.
const controllers = new Map<string, ControllerConstructor>([
  ["accounts", AccountsController],
  ["account-tooltip", AccountTooltipController],
  ["account-setup", AccountSetupController],
  ["alerts", AlertsController],
  ["balance-check", BalanceCheckController],
  ["page-position", PagePositionController],
  ["auto-read-notification", AutoReadNotificationController],
  ["bottom-sheet", BottomSheetController],
  ["budget-allocation-bar", BudgetAllocationBarController],
  ["budget-category-filter", BudgetCategoryFilterController],
  ["coloriser", ColoriserController],
  ["dialog", DialogController],
  ["donut", DonutController],
  ["categories", CategoriesController],
  ["categorise", CategoriseController],
  ["center", CenterController],
  ["form-redirect", FormRedirectController],
  ["forms", FormsController],
  ["institutions", InstitutionsController],
  ["month-strip", MonthStripController],
  ["notifications-panel", NotificationsPanelController],
  ["notifications-trigger", NotificationsTriggerController],
  ["payload-example", PayloadExampleController],
  ["push", PushController],
  ["quick-categorisation", QuickCategorisationController],
  ["pwa", PwaController],
  ["turnstile", TurnstileController],
  ["scroll-to-top", ScrollToTopController],
  ["selects", SelectsController],
  ["svg-bar-chart", SvgBarChartController],
  ["tabs", TabsController],
  ["transaction-analysis", TransactionAnalysisController],
  ["transaction-filters", TransactionFiltersController],
  ["turbo-refresh", TurboRefreshController],
  ["frame-spinner", FrameSpinnerController],
  ["toggle", ToggleController],
]);

// Unload the existing application if one exists.
function stopStimulus() {
  if (window.Stimulus) {
    window.Stimulus.unload(...controllers.keys());
    window.Stimulus.stop();
    console.info("Stimulus stopped and cleaned up");
    window.Stimulus = undefined;
  }
}

function startStimulus() {
  stopStimulus();
  const Stimulus = Application.start();
  // Register controllers
  controllers.forEach((controller, name) => {
    Stimulus.register(name, controller);
  });

  window.Stimulus = Stimulus;
}

startStimulus();

Turbo.config.drive.progressBarDelay = 0;
Turbo.start();
